/**
 * Reddit Advertising — Sync Pipeline Orchestration + Async Chunking (Story 19.11, Tasks 2 + 3)
 *
 * Two responsibilities, one module:
 *
 *   1. **Sync pipeline runner** (`runRedditAdsSyncPipeline`)
 *      Issues a single `POST /ad_accounts/{id}/reports` per pipeline, parses
 *      the response via `parseRedditReportResponse`, and surfaces the
 *      classified error (or success) for the orchestrator.
 *
 *   2. **Default 6-pipeline parallel fan-out** (`runRedditAdsParallelPipelines`)
 *      Runs Overview / Campaigns / AdGroups / Ads / Subreddits / CountryRegion
 *      concurrently via `Promise.all`. Applies multi-fatal-priority race
 *      resolution (deterministic — never first-arriving / last-arriving).
 *
 *   3. **Client-side temporal partitioning** (`runRedditAdsAsyncChunked`)
 *      Reddit v3 has NO async-with-poll; we partition the date range into
 *      ≤30-day chunks, fire ≤6 in parallel, dedupe at merge time. Used when
 *      breakdown count > 3, date range > 90d, or other overflow signals.
 *
 *   4. **Reddit metric transformer** (`parseRedditMetric`)
 *      UPPERCASE_SNAKE → camelCase with dual currency divisors:
 *        ÷1,000,000 (micro) for SPEND/CPC/eCPA/COST_PER_*_VIEW/...
 *        ÷100      (cents) for CONVERSION_*_TOTAL_VALUE/AVG_VALUE
 *      Percentages defensive-normalized to [0, 1].
 *
 *   5. **Discovery helpers** (`discoverActiveCampaigns`, `fetchAdAccountMetadata`)
 *      Used pre-fan-out to scope sub-entity fetches and stamp
 *      `metadata.currencyCode` / `timeZoneId` / `customEventLabels`.
 */

import type { AdapterErrorCode } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";
import {
  REDDIT_ADS_API_BASE,
  safeFetchReddit,
  type RedditRateLimit,
} from "./api";
import { getRedditAdsKeyByApiField } from "./catalogue";
import {
  RedditAdsApiCallError,
  redditAdsErrorTypeToAdapterCode,
} from "./errors";
import {
  REDDIT_ADS_MAX_ASYNC_CHUNK_DAYS,
  REDDIT_ADS_MAX_PARALLEL_CHUNKS,
  REDDIT_ADS_MAX_SUBREDDITS_PER_REPORT,
  REDDIT_REPORTS_TIMEOUT_MS,
  buildRedditReportRequest,
  buildRedditReportRequestForAsyncChunk,
  computeRedditAsyncChunkCount,
  type RedditAdsReportRequestOptions,
  type RedditAdsReportRequest,
  type RedditAdsReportType,
} from "./reports";

// ============================================================================
// Constants
// ============================================================================

/** Pagination cap for active-campaign discovery (5 pages × 200 = 1000). */
export const REDDIT_ADS_MAX_CAMPAIGNS_PAGES = 5;
/** Default page size for the campaigns endpoint (Reddit v3 max = 200). */
export const REDDIT_ADS_CAMPAIGNS_PAGE_SIZE = 200;
/** Top-N selection caps for entity-scoped pipelines. */
export const REDDIT_ADS_TOP_CAMPAIGNS_LIMIT = 20;
export const REDDIT_ADS_TOP_AD_GROUPS_LIMIT = 20;
export const REDDIT_ADS_TOP_ADS_LIMIT = 50;

/**
 * Multi-fatal priority order — when 2+ pipelines fail with fatal codes, the
 * adapter resolves deterministically by selecting the highest-priority code.
 *
 * Matches Snapchat 19.8 + Pinterest 19.5 + MS Ads 19.2 ordering with the
 * Reddit-specific addition of `api_not_enabled` between `permission_denied`
 * and `rate_limited` (sales-gating bleed-through per AC #4).
 */
export const REDDIT_FATAL_ERROR_PRIORITY: ReadonlyArray<AdapterErrorCode> = [
  "token_expired",
  "token_invalid",
  "permission_denied",
  "api_not_enabled",
  "rate_limited",
  "provider_unavailable",
];

/** Set membership lookup for fatal-error checks. */
export const REDDIT_FATAL_ERROR_CODES: ReadonlySet<AdapterErrorCode> = new Set(
  REDDIT_FATAL_ERROR_PRIORITY
);

// ============================================================================
// Types
// ============================================================================

export interface RedditAdsSyncPipelineOptions
  extends RedditAdsReportRequestOptions {
  readonly reportType: RedditAdsReportType;
}

export interface RedditAdsPipelineWarning {
  readonly reportType: string;
  readonly reason: string;
}

export type RedditAdsPipelineSuccess = {
  readonly ok: true;
  readonly reportType: RedditAdsReportType;
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly warnings: ReadonlyArray<RedditAdsPipelineWarning>;
  readonly rateLimit: RedditRateLimit | null;
};

export type RedditAdsPipelineFailure = {
  readonly ok: false;
  readonly reportType: RedditAdsReportType;
  readonly fatal: boolean;
  readonly code: AdapterErrorCode;
  readonly message: string;
  readonly retryable: boolean;
};

export type RedditAdsPipelineResult =
  | RedditAdsPipelineSuccess
  | RedditAdsPipelineFailure;

// ============================================================================
// Reddit metric transformer
// ============================================================================

/** Micro-currency fields (Reddit emits as integer micros — divide by 1,000,000). */
const MICRO_CURRENCY_FIELDS: ReadonlySet<string> = new Set([
  "SPEND",
  "CPC",
  "CPV",
  "ECPM",
  "ROAS_NUMERATOR",
  "KEY_CONVERSION_ECPA",
  "COST_PER_3_SECOND_VIEW",
  "COST_PER_6_SECOND_VIEW",
  "COST_PER_15_SECOND_VIEW",
  "COST_PER_COMPLETED_VIEW",
  "APP_INSTALL_ECPA",
  "APP_INSTALL_MMP_ECPA",
  "APP_INSTALL_SKAN_ECPI",
  "APP_REDOWNLOAD_SKAN_ECPI",
  "APP_INSTALL_SKAN_TOTAL_ECPI",
]);

/**
 * Pure helper: divide micro-currency value by 1,000,000.
 * Distinct from `parseConversionValue` — they are NOT aliased.
 */
export function parseMicroCurrency(value: number): number {
  return Number.parseFloat((value / 1_000_000).toFixed(6));
}

/**
 * Pure helper: divide cents value by 100 (Reddit's conversion-value divisor).
 *
 * MUST be a separate function from `parseMicroCurrency`. Tests assert that
 * `SPEND` is divided by 1M and `CONVERSION_PURCHASE_TOTAL_VALUE` is divided by
 * 100 in the same `parseRedditMetric` pass — getting them mixed up corrupts
 * conversion-value reporting by 4 orders of magnitude.
 */
export function parseConversionValue(value: number): number {
  return Number.parseFloat((value / 100).toFixed(4));
}

/**
 * Defensive percentage normalization: Reddit's percentage emission is
 * inconsistent across third-party connectors — sometimes `[0, 1]` fractions,
 * sometimes `[0, 100]` display-percent. We normalize to `[0, 1]`.
 */
export function parseRedditPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value > 1) return Number.parseFloat((value / 100).toFixed(6));
  if (value < 0) return 0;
  return Number.parseFloat(value.toFixed(6));
}

/** Returns `true` iff the field is a Reddit conversion-value (÷100) field. */
function isConversionValueField(field: string): boolean {
  if (field.startsWith("CONVERSION_") && field.endsWith("_TOTAL_VALUE")) {
    return true;
  }
  if (field.startsWith("CONVERSION_") && field.endsWith("_AVG_VALUE")) {
    return true;
  }
  if (field === "CONVERSION_SIGNUP_AVG_VALUE") return true;
  if (field === "CONVERSION_SIGNUP_TOTAL_VALUE") return true;
  return false;
}

/** Returns `true` iff the field is a micro-currency (÷1,000,000) field. */
function isMicroCurrencyField(field: string): boolean {
  if (MICRO_CURRENCY_FIELDS.has(field)) return true;
  // _ECPA / _ECPI suffixes uniformly indicate micro-currency
  if (field.endsWith("_ECPA")) return true;
  if (field.endsWith("_ECPI")) return true;
  return false;
}

/** Returns `true` iff the field is a percentage field. */
function isPercentField(field: string): boolean {
  if (field === "CTR") return true;
  if (field === "VIDEO_COMPLETION_RATE") return true;
  if (field === "VIDEO_VIEW_RATE") return true;
  if (field.startsWith("VIDEO_VIEW_RATE_")) return true;
  if (field === "KEY_CONVERSION_RATE") return true;
  if (field.startsWith("CONVERSION_") && field.endsWith("_CONVERSION_RATE")) return true;
  if (field === "APP_INSTALL_CVR") return true;
  if (field === "APP_INSTALL_SKAN_CVR") return true;
  if (field === "APP_INSTALL_SKAN_TOTAL_CVR") return true;
  return false;
}

/** Returns `true` iff the field is a non-numeric breakdown identifier. */
function isBreakdownField(field: string): boolean {
  return (
    field === "AD_ACCOUNT_ID" ||
    field === "CAMPAIGN_ID" ||
    field === "AD_GROUP_ID" ||
    field === "AD_ID" ||
    field === "DATE" ||
    field === "HOUR" ||
    field === "COUNTRY" ||
    field === "REGION" ||
    field === "DMA" ||
    field === "METRO" ||
    field === "GENDER" ||
    field === "INTEREST" ||
    field === "OS_TYPE" ||
    field === "SUBREDDIT" ||
    field === "COMMUNITY" ||
    field === "KEYWORD" ||
    field === "PLACEMENT" ||
    field === "CAROUSEL_CARD" ||
    field === "GALLERY_ITEM_ID" ||
    field === "CLICK_ATTRIBUTION_WINDOW" ||
    field === "VIEW_ATTRIBUTION_WINDOW"
  );
}

/** Convert a Reddit UPPERCASE_SNAKE_CASE field to a camelCase output key. */
function uppercaseSnakeToCamel(field: string): string {
  // Catalogue lookup wins when present (handles canonical aliasing).
  const catKey = getRedditAdsKeyByApiField(field);
  if (catKey) return catKey;
  // Fallback for breakdown fields and unrecognized fields.
  const lower = field.toLowerCase();
  return lower.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

/**
 * Apply the Reddit-specific transformer chain to a single raw value:
 *   - micro-currency → ÷1,000,000
 *   - cents          → ÷100
 *   - percent        → normalize to [0, 1]
 *   - integer/decimal → emit unchanged (with light precision-clamp on decimals)
 *
 * `null` values from Reddit are normalized to 0 (rather than `null`) so
 * widget rendering doesn't have to deal with `null`/`undefined` distinctions.
 */
export function parseRedditMetric(field: string, raw: unknown): unknown {
  if (raw === null || raw === undefined) {
    if (isBreakdownField(field)) return raw; // breakdowns may be null
    return 0;
  }

  // Breakdowns: pass through as-is (they're strings/numbers/dates).
  if (isBreakdownField(field)) {
    return raw;
  }

  if (typeof raw === "number") {
    if (isMicroCurrencyField(field)) return parseMicroCurrency(raw);
    if (isConversionValueField(field)) return parseConversionValue(raw);
    if (isPercentField(field)) return parseRedditPercent(raw);
    // Integer fields and decimals: clamp to 4-decimal precision to avoid drift.
    return Number.parseFloat(raw.toFixed(4));
  }

  // Non-numeric values pass through unchanged (defensive — Reddit might emit
  // strings for some breakdown-bearing fields).
  return raw;
}

// ============================================================================
// Response parser
// ============================================================================

interface RawReportResponse {
  data?: ReadonlyArray<Record<string, unknown>>;
  pagination?: { next?: string | null };
}

/**
 * Parse a Reddit `POST /reports` response into normalised camelCase records.
 *
 * Stamps `_reportType` discriminator on every row so downstream callers can
 * disambiguate concatenated multi-pipeline output. Caller passes the report
 * type so this function stays pure (no global state).
 */
export function parseRedditReportResponse(
  json: unknown,
  reportType: RedditAdsReportType
): { records: Array<Record<string, unknown>> } {
  const records: Array<Record<string, unknown>> = [];
  const body = (json ?? {}) as RawReportResponse;
  const rows = Array.isArray(body.data) ? body.data : [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) continue;
    const out: Record<string, unknown> = { _reportType: reportType };
    for (const [k, v] of Object.entries(row)) {
      const camel = uppercaseSnakeToCamel(k);
      out[camel] = parseRedditMetric(k, v);
    }
    records.push(out);
  }
  return { records };
}

// ============================================================================
// Single sync pipeline runner
// ============================================================================

/** Issue a single `POST /reports` and return classified records or failure. */
export async function runRedditAdsSyncPipeline(
  accessToken: string,
  userAgent: string,
  adAccountId: string,
  options: RedditAdsSyncPipelineOptions
): Promise<RedditAdsPipelineResult> {
  let request: RedditAdsReportRequest;
  try {
    request = buildRedditReportRequest(adAccountId, options);
  } catch (err) {
    return {
      ok: false,
      reportType: options.reportType,
      fatal: false,
      code: "invalid_response",
      message: sanitizeErrorMessage(
        err instanceof Error ? err.message : String(err)
      ),
      retryable: false,
    };
  }

  try {
    const result = await safeFetchReddit(
      request.url,
      accessToken,
      userAgent,
      {
        method: "POST",
        body: JSON.stringify(request.body),
      },
      REDDIT_REPORTS_TIMEOUT_MS,
      `Reddit Ads ${options.reportType} report failed`
    );

    let json: unknown;
    try {
      json = await result.response.json();
    } catch {
      return {
        ok: false,
        reportType: options.reportType,
        fatal: false,
        code: "invalid_response",
        message: `Reddit Ads ${options.reportType} response was not JSON`,
        retryable: false,
      };
    }

    const parsed = parseRedditReportResponse(json, options.reportType);
    const warnings: RedditAdsPipelineWarning[] = request.warnings.map((w) => ({
      reportType: options.reportType,
      reason: w.reason,
    }));

    if (parsed.records.length === 0) {
      warnings.push({
        reportType: options.reportType,
        reason: `Reddit Ads ${options.reportType} returned 0 records`,
      });
    }

    return {
      ok: true,
      reportType: options.reportType,
      records: parsed.records,
      warnings,
      rateLimit: result.rateLimit,
    };
  } catch (err) {
    if (err instanceof RedditAdsApiCallError) {
      const code = redditAdsErrorTypeToAdapterCode(err.type);
      return {
        ok: false,
        reportType: options.reportType,
        fatal: REDDIT_FATAL_ERROR_CODES.has(code),
        code,
        message: sanitizeErrorMessage(err.message),
        retryable: err.retryable,
      };
    }
    const message = err instanceof Error ? err.message : String(err);
    return {
      ok: false,
      reportType: options.reportType,
      fatal: false,
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Reddit Ads ${options.reportType} pipeline failed: ${message}`
      ),
      retryable: false,
    };
  }
}

// ============================================================================
// Multi-fatal priority race resolver
// ============================================================================

/**
 * Given a list of failure codes, return the highest-priority fatal code per
 * `REDDIT_FATAL_ERROR_PRIORITY`. Returns null if no fatal codes are present.
 *
 * **Determinism guarantee:** the same set of codes yields the same result
 * regardless of arrival order — required for AC #19 race resolution.
 */
export function resolveRedditFatalPriority(
  codes: ReadonlyArray<AdapterErrorCode>
): AdapterErrorCode | null {
  const present = new Set(codes);
  for (const candidate of REDDIT_FATAL_ERROR_PRIORITY) {
    if (present.has(candidate)) return candidate;
  }
  return null;
}

// ============================================================================
// Default 6-pipeline parallel fan-out
// ============================================================================

/**
 * Per-fan-out pipeline definition. Encodes the breakdowns/fields that drive
 * each report-type's behavior.
 */
export interface RedditAdsFanOutPlan {
  readonly reportType: RedditAdsReportType;
  readonly breakdowns: RedditAdsReportRequestOptions["breakdowns"];
}

/**
 * Default 6-pipeline plan (AC #3). Caller provides the field list — keeping
 * field selection at the orchestrator layer makes it easy to switch between
 * "delivery-only" and "with-conversions" defaults without edit-the-file churn.
 */
export const DEFAULT_REDDIT_ADS_FAN_OUT_PLAN: ReadonlyArray<RedditAdsFanOutPlan> = [
  { reportType: "overview", breakdowns: ["DATE"] },
  { reportType: "campaigns", breakdowns: ["CAMPAIGN_ID", "DATE"] },
  { reportType: "adGroups", breakdowns: ["AD_GROUP_ID", "DATE"] },
  { reportType: "ads", breakdowns: ["AD_ID", "DATE"] },
  { reportType: "subreddits", breakdowns: ["SUBREDDIT"] },
  { reportType: "countryRegion", breakdowns: ["COUNTRY", "REGION"] },
];

/**
 * Stable lex-asc-by-id sort over arbitrary record arrays. Used to make
 * top-N selections deterministic across re-runs (the same request to Reddit
 * returns rows in undefined order; we must impose order client-side).
 */
function stableLexSortById<T extends Record<string, unknown>>(
  records: ReadonlyArray<T>,
  idKey: string
): T[] {
  return [...records].sort((a, b) => {
    const aId = String((a as Record<string, unknown>)[idKey] ?? "");
    const bId = String((b as Record<string, unknown>)[idKey] ?? "");
    return aId.localeCompare(bId, undefined, { sensitivity: "base" });
  });
}

/**
 * Pick top-N records by a numeric metric, with secondary lex-asc-by-id stable
 * sort for deterministic re-run selection.
 */
export function pickTopByMetric<T extends Record<string, unknown>>(
  records: ReadonlyArray<T>,
  metricKey: string,
  idKey: string,
  n: number
): T[] {
  // First sort lex-asc-id to make ties deterministic.
  const lexSorted = stableLexSortById(records, idKey);
  // Then stable sort descending by metric — preserves the lex order on ties.
  const sorted = [...lexSorted].sort((a, b) => {
    const aV = Number((a as Record<string, unknown>)[metricKey] ?? 0);
    const bV = Number((b as Record<string, unknown>)[metricKey] ?? 0);
    return bV - aV;
  });
  return sorted.slice(0, n);
}

/** Top-20 campaigns by spend with lex-asc-id stability. */
export function pickTopCampaignsBySpend<T extends Record<string, unknown>>(
  records: ReadonlyArray<T>,
  n: number = REDDIT_ADS_TOP_CAMPAIGNS_LIMIT
): T[] {
  return pickTopByMetric(records, "spend", "campaignId", n);
}

/** Top-20 ad groups by spend with lex-asc-id stability. */
export function pickTopAdGroupsBySpend<T extends Record<string, unknown>>(
  records: ReadonlyArray<T>,
  n: number = REDDIT_ADS_TOP_AD_GROUPS_LIMIT
): T[] {
  return pickTopByMetric(records, "spend", "adGroupId", n);
}

/** Top-50 ads by impressions with lex-asc-id stability. */
export function pickTopAdsByImpressions<T extends Record<string, unknown>>(
  records: ReadonlyArray<T>,
  n: number = REDDIT_ADS_TOP_ADS_LIMIT
): T[] {
  return pickTopByMetric(records, "impressions", "adId", n);
}

/** Top-200 subreddits by impressions with lex-asc-name stability. */
export function pickTopSubredditsByImpressions<
  T extends Record<string, unknown>,
>(
  records: ReadonlyArray<T>,
  n: number = REDDIT_ADS_MAX_SUBREDDITS_PER_REPORT
): T[] {
  return pickTopByMetric(records, "impressions", "subreddit", n);
}

// ============================================================================
// Discovery helpers
// ============================================================================

interface RawCampaign {
  id?: string;
  name?: string;
  configured_status?: string;
}

interface RawCampaignsResponse {
  data?: RawCampaign[];
  pagination?: { next?: string | null };
}

/** Discover active campaigns under an ad account, capped at 1,000. */
export async function discoverActiveCampaigns(
  accessToken: string,
  userAgent: string,
  adAccountId: string
): Promise<{ campaignIds: string[]; partial: boolean; totalCount: number }> {
  const collected: string[] = [];
  let pageToken: string | undefined;
  let partial = false;
  let totalCount = 0;
  for (let page = 0; page < REDDIT_ADS_MAX_CAMPAIGNS_PAGES; page++) {
    const url = new URL(
      `${REDDIT_ADS_API_BASE}/ad_accounts/${encodeURIComponent(adAccountId)}/campaigns`
    );
    url.searchParams.set("page.size", String(REDDIT_ADS_CAMPAIGNS_PAGE_SIZE));
    if (pageToken) url.searchParams.set("page.token", pageToken);

    const result = await safeFetchReddit(
      url.toString(),
      accessToken,
      userAgent,
      { method: "GET" },
      undefined,
      `Reddit Ads campaigns discovery failed`
    );
    let json: RawCampaignsResponse;
    try {
      json = (await result.response.json()) as RawCampaignsResponse;
    } catch {
      throw new Error("Invalid Reddit Ads campaigns response: not JSON");
    }
    const items = Array.isArray(json.data) ? json.data : [];
    totalCount += items.length;
    for (const c of items) {
      const cid = typeof c.id === "string" ? c.id : "";
      if (!cid) continue;
      // Filter out non-active campaigns
      const status = typeof c.configured_status === "string" ? c.configured_status : "";
      if (status === "PAUSED" || status === "DELETED" || status === "ARCHIVED") {
        continue;
      }
      collected.push(cid);
    }
    const next = json.pagination?.next;
    if (!next || typeof next !== "string" || next.length === 0) break;
    pageToken = next;
    if (page === REDDIT_ADS_MAX_CAMPAIGNS_PAGES - 1) partial = true;
  }
  // Stable lex-asc sort for deterministic selection
  collected.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  return { campaignIds: collected, partial, totalCount };
}

interface RawAdAccountResponse {
  data?: {
    id?: string;
    currency?: string;
    time_zone_id?: string;
    custom_event_labels?: Record<string, unknown>;
    name?: string;
  };
}

export interface RedditAdAccountMetadata {
  readonly currencyCode: string;
  readonly timeZoneId: string;
  readonly customEventLabels: Record<string, string> | null;
  readonly name: string;
}

/**
 * Fetch ad-account metadata (currency / time_zone_id / custom event labels).
 * Used by the adapter's fetchMetrics to stamp `metadata` on the result.
 *
 * Defaults `time_zone_id` to "UTC" when missing (with implicit warning at the
 * orchestrator level via the caller — `runRedditAdsParallelPipelines` adds a
 * warning when this happens).
 */
export async function fetchRedditAdAccountMetadata(
  accessToken: string,
  userAgent: string,
  adAccountId: string
): Promise<RedditAdAccountMetadata> {
  const url = `${REDDIT_ADS_API_BASE}/ad_accounts/${encodeURIComponent(adAccountId)}`;
  const result = await safeFetchReddit(
    url,
    accessToken,
    userAgent,
    { method: "GET" },
    undefined,
    "Reddit Ads ad-account metadata failed"
  );
  let json: RawAdAccountResponse;
  try {
    json = (await result.response.json()) as RawAdAccountResponse;
  } catch {
    return { currencyCode: "", timeZoneId: "UTC", customEventLabels: null, name: "" };
  }
  const acct = json.data ?? {};
  let labels: Record<string, string> | null = null;
  if (
    acct.custom_event_labels &&
    typeof acct.custom_event_labels === "object" &&
    !Array.isArray(acct.custom_event_labels)
  ) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(acct.custom_event_labels)) {
      if (typeof v === "string") out[k] = v;
    }
    if (Object.keys(out).length > 0) labels = out;
  }
  return {
    currencyCode: typeof acct.currency === "string" ? acct.currency : "",
    timeZoneId:
      typeof acct.time_zone_id === "string" && acct.time_zone_id.length > 0
        ? acct.time_zone_id
        : "UTC",
    customEventLabels: labels,
    name: typeof acct.name === "string" ? acct.name : "",
  };
}

// ============================================================================
// Run the default 6-pipeline parallel fan-out
// ============================================================================

export interface RunRedditAdsParallelInput {
  readonly accessToken: string;
  readonly userAgent: string;
  readonly adAccountId: string;
  readonly fields: ReadonlyArray<string>;
  readonly startsAt: Date;
  readonly endsAt: Date;
  readonly timeZoneId: string;
  readonly fanOutPlan?: ReadonlyArray<RedditAdsFanOutPlan>;
}

export interface RunRedditAdsParallelResult {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly warnings: ReadonlyArray<RedditAdsPipelineWarning>;
  readonly fatalCode: AdapterErrorCode | null;
  readonly fatalMessage: string | null;
  readonly rateLimit: RedditRateLimit | null;
  readonly perPipeline: ReadonlyArray<RedditAdsPipelineResult>;
}

/**
 * Run the 6 default sync pipelines in parallel via `Promise.all`.
 *
 *   - On any fatal error: returns the highest-priority fatal code
 *     (deterministic per `REDDIT_FATAL_ERROR_PRIORITY` — race-resolution per
 *     AC #19), with empty `records[]`.
 *   - On non-fatal errors / empty pipelines: surfaces empty `records[]` for
 *     that pipeline plus a `warnings[]` entry.
 *   - On success: concatenates `records[]` across all pipelines.
 *   - Subreddits pipeline is post-fetch capped to top-N by impressions
 *     (AC #19 — `REDDIT_ADS_MAX_SUBREDDITS_PER_REPORT = 200`).
 */
export async function runRedditAdsParallelPipelines(
  input: RunRedditAdsParallelInput
): Promise<RunRedditAdsParallelResult> {
  const plan = input.fanOutPlan ?? DEFAULT_REDDIT_ADS_FAN_OUT_PLAN;

  const pipelinePromises = plan.map((entry) =>
    runRedditAdsSyncPipeline(input.accessToken, input.userAgent, input.adAccountId, {
      reportType: entry.reportType,
      breakdowns: entry.breakdowns,
      fields: input.fields,
      startsAt: input.startsAt,
      endsAt: input.endsAt,
      timeZoneId: input.timeZoneId,
    })
  );
  const results = await Promise.all(pipelinePromises);

  // Multi-fatal race resolution
  const fatalCodes: AdapterErrorCode[] = [];
  for (const r of results) {
    if (!r.ok && r.fatal) fatalCodes.push(r.code);
  }
  const fatalCode = resolveRedditFatalPriority(fatalCodes);
  if (fatalCode !== null) {
    const fatalResult = results.find(
      (r): r is RedditAdsPipelineFailure => !r.ok && r.fatal && r.code === fatalCode
    );
    return {
      records: [],
      warnings: [],
      fatalCode,
      fatalMessage: fatalResult?.message ?? null,
      rateLimit: null,
      perPipeline: results,
    };
  }

  // Aggregate non-fatal results.
  const allRecords: Array<Record<string, unknown>> = [];
  const allWarnings: RedditAdsPipelineWarning[] = [];
  let lastRateLimit: RedditRateLimit | null = null;
  for (const r of results) {
    if (r.ok) {
      let pipelineRecords: ReadonlyArray<Record<string, unknown>> = r.records;
      if (r.reportType === "subreddits") {
        const before = pipelineRecords.length;
        pipelineRecords = pickTopSubredditsByImpressions(pipelineRecords);
        if (before > pipelineRecords.length) {
          allWarnings.push({
            reportType: "subreddits",
            reason: `Showing top ${pipelineRecords.length} of ${before} subreddits — contact support to enumerate all`,
          });
        }
      }
      allRecords.push(...pipelineRecords);
      allWarnings.push(...r.warnings);
      if (r.rateLimit !== null) lastRateLimit = r.rateLimit;
    } else {
      // Non-fatal failure → surface a warning
      allWarnings.push({
        reportType: r.reportType,
        reason: `Pipeline ${r.reportType} failed (non-fatal): ${r.message}`,
      });
    }
  }

  return {
    records: allRecords,
    warnings: allWarnings,
    fatalCode: null,
    fatalMessage: null,
    rateLimit: lastRateLimit,
    perPipeline: results,
  };
}

// ============================================================================
// Async chunked fan-out (Task 3)
// ============================================================================

export interface RunRedditAdsAsyncChunkedInput {
  readonly accessToken: string;
  readonly userAgent: string;
  readonly adAccountId: string;
  readonly reportType: RedditAdsReportType;
  readonly fields: ReadonlyArray<string>;
  readonly breakdowns: RedditAdsReportRequestOptions["breakdowns"];
  readonly startsAt: Date;
  readonly endsAt: Date;
  readonly timeZoneId: string;
  readonly chunkDays?: number;
  readonly maxParallel?: number;
}

export interface RunRedditAdsAsyncChunkedResult {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly warnings: ReadonlyArray<RedditAdsPipelineWarning>;
  readonly fatalCode: AdapterErrorCode | null;
  readonly fatalMessage: string | null;
  readonly chunkCount: number;
}

/** Canonicalize alias breakdowns inline (mirrors `reports.ts` `normalizeBreakdown`). */
function normalizeBreakdownLocal(input: string): string {
  if (input === "ADGROUP_ID") return "AD_GROUP_ID";
  if (input === "ACCOUNT_ID") return "AD_ACCOUNT_ID";
  return input;
}

/**
 * Build a composite key for chunked-merge dedup. Uses the report-type plus
 * a stable hash of the breakdown values for the row.
 *
 * **Alias normalization:** the input `breakdowns` array may contain
 * `ADGROUP_ID` / `ACCOUNT_ID` aliases. Records are keyed by the CANONICAL
 * camelCase form (`adGroupId`, `adAccountId`) — we must normalize aliases here
 * before catalogue lookup, otherwise dedup keys mismatch and rows pass through
 * un-deduped (Round 1 senior-dev review finding — alias-form leak).
 */
function buildDedupKey(
  reportType: RedditAdsReportType,
  row: Record<string, unknown>,
  adAccountId: string,
  breakdowns: ReadonlyArray<string>
): string {
  const parts: string[] = [reportType, adAccountId];
  for (const b of breakdowns) {
    const canonical = normalizeBreakdownLocal(b);
    // Convert UPPERCASE breakdown to camelCase for record lookup
    const camelKey = uppercaseSnakeToCamel(canonical);
    const v = row[camelKey];
    parts.push(`${camelKey}=${String(v ?? "")}`);
  }
  return parts.join("|");
}

/**
 * Client-side temporal partitioning for Reddit's "async" path. Reddit v3 has
 * NO async-with-poll primitive; we partition the date range into ≤30-day
 * chunks, fire ≤6 in parallel, dedupe at merge time.
 *
 * **WARNING:** This code path WILL exceed the 30s Cloudflare Workers
 * HTTP-trigger wall-clock budget when chunk count > 4 (4 chunks × 8s peak =
 * 32s); invoke ONLY from queue consumers (15min budget) or cron handlers.
 *
 * Per-chunk single retry on transient failures (5xx/429) is handled via
 * `safeFetchReddit`'s 3-attempt budget — this function does NOT add an extra
 * retry layer to keep the chunk budget bounded.
 */
export async function runRedditAdsAsyncChunked(
  input: RunRedditAdsAsyncChunkedInput
): Promise<RunRedditAdsAsyncChunkedResult> {
  // Dev-time signal: only emit on Node-hosted runtimes (Vitest, dev). On
  // Workers `process` is undefined; we guard via globalThis to avoid type-
  // import dependencies on @types/node.
  const proc = (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }).process;
  if (proc && proc.env && proc.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      "[reddit-ads] runRedditAdsAsyncChunked: HTTP-direct invocation will exceed 30s budget for >4 chunks. Use queue/cron context."
    );
  }

  const chunkDays = input.chunkDays ?? REDDIT_ADS_MAX_ASYNC_CHUNK_DAYS;
  const maxParallel = input.maxParallel ?? REDDIT_ADS_MAX_PARALLEL_CHUNKS;
  const totalChunks = computeRedditAsyncChunkCount(
    input.startsAt,
    input.endsAt,
    chunkDays
  );

  const baseOptions: RedditAdsReportRequestOptions = {
    fields: input.fields,
    breakdowns: input.breakdowns,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    timeZoneId: input.timeZoneId,
  };

  const allRecords: Array<Record<string, unknown>> = [];
  const allWarnings: RedditAdsPipelineWarning[] = [];
  const seen = new Set<string>();
  const fatalCodes: AdapterErrorCode[] = [];
  const fatalMessages: string[] = [];

  // Fire chunks in batches of `maxParallel`.
  for (let batchStart = 0; batchStart < totalChunks; batchStart += maxParallel) {
    const batchEnd = Math.min(batchStart + maxParallel, totalChunks);
    const batchPromises: Array<Promise<RedditAdsPipelineResult>> = [];
    for (let i = batchStart; i < batchEnd; i++) {
      // Pre-validate the chunk request via the chunk builder; if it throws
      // (e.g. HOUR breakdown overflow inside a chunk), wrap as non-fatal.
      let chunkOptions: RedditAdsReportRequestOptions;
      try {
        const req = buildRedditReportRequestForAsyncChunk(
          input.adAccountId,
          baseOptions,
          i,
          totalChunks,
          chunkDays
        );
        // Reconstruct options from the request for the pipeline runner
        chunkOptions = {
          fields: req.body.data.fields,
          breakdowns: req.body.data.breakdowns,
          startsAt: new Date(req.body.data.starts_at),
          endsAt: new Date(req.body.data.ends_at),
          timeZoneId: req.body.data.time_zone_id,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        batchPromises.push(
          Promise.resolve({
            ok: false,
            reportType: input.reportType,
            fatal: false,
            code: "invalid_response",
            message: sanitizeErrorMessage(message),
            retryable: false,
          })
        );
        continue;
      }
      batchPromises.push(
        runRedditAdsSyncPipeline(
          input.accessToken,
          input.userAgent,
          input.adAccountId,
          { ...chunkOptions, reportType: input.reportType }
        )
      );
    }
    const batchResults = await Promise.all(batchPromises);
    for (const r of batchResults) {
      if (r.ok) {
        let dupes = 0;
        for (const row of r.records) {
          const key = buildDedupKey(
            input.reportType,
            row,
            input.adAccountId,
            input.breakdowns as ReadonlyArray<string>
          );
          if (seen.has(key)) {
            dupes++;
            continue;
          }
          seen.add(key);
          allRecords.push(row);
        }
        if (dupes > 0) {
          allWarnings.push({
            reportType: input.reportType,
            reason: `Async-chunk merge deduped ${dupes} boundary-day rows`,
          });
        }
        // Forward request-builder warnings up
        allWarnings.push(...r.warnings);
      } else if (r.fatal) {
        fatalCodes.push(r.code);
        fatalMessages.push(r.message);
      } else {
        allWarnings.push({
          reportType: input.reportType,
          reason: `Async chunk failed (non-fatal): ${r.message}`,
        });
      }
    }

    // Fail fast on fatal codes within a batch.
    if (fatalCodes.length > 0) break;
  }

  const fatalCode = resolveRedditFatalPriority(fatalCodes);
  return {
    records: allRecords,
    warnings: allWarnings,
    fatalCode,
    fatalMessage: fatalCode !== null ? fatalMessages.join("; ") : null,
    chunkCount: totalChunks,
  };
}

